import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { LogOut, RefreshCw, Trash2, Mail, Phone, Sparkles, MessageSquare, Search, Loader2 } from "lucide-react";

const TIER_LABEL = {
  onda: "L'Onda",
  tsunami: "Lo Tsunami",
  oceano: "L'Oceano",
};

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
}

export default function AdminDashboard() {
  const { user, logout, authAxios } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | contact | quiz
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { kind: filter };
      const [{ data: leadsData }, { data: statsData }] = await Promise.all([
        authAxios.get("/admin/leads", { params }),
        authAxios.get("/admin/stats"),
      ]);
      setLeads(leadsData.items || []);
      setStats(statsData);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore caricamento lead");
    } finally { setLoading(false); }
  }, [filter, authAxios]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!query.trim()) return leads;
    const q = query.trim().toLowerCase();
    return leads.filter((l) =>
      (l.name || "").toLowerCase().includes(q) ||
      (l.email || "").toLowerCase().includes(q) ||
      (l.message || "").toLowerCase().includes(q)
    );
  }, [leads, query]);

  const remove = async (id) => {
    if (!window.confirm("Eliminare definitivamente questo lead?")) return;
    try {
      await authAxios.delete(`/admin/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Lead eliminato");
    } catch (e) {
      toast.error("Eliminazione non riuscita");
    }
  };

  const handleLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div data-testid="admin-dashboard-page" className="min-h-screen bg-[#05050A] text-slate-100">
      <div className="pointer-events-none fixed inset-0 -z-10 opacity-60"
        style={{ background: "radial-gradient(60% 40% at 10% 10%, rgba(147,51,234,0.15) 0%, transparent 60%), radial-gradient(50% 40% at 90% 90%, rgba(59,130,246,0.13) 0%, transparent 60%)" }}
      />

      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-xl bg-[#05050A]/70 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-[68px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/assets/logo.png" alt="Social Whale" className="h-9 w-9 object-contain drop-shadow-[0_0_18px_rgba(147,51,234,0.5)]" />
            <div className="hidden sm:block">
              <div className="font-display font-bold text-white text-lg leading-none">Social<span className="text-gradient">Whale</span></div>
              <div className="text-[10px] uppercase tracking-widest text-purple-300/80 mt-1">Admin</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden md:block text-xs text-slate-400" data-testid="admin-user-email">{user?.email}</div>
            <button onClick={load} className="btn-ghost text-sm py-2 px-3" data-testid="admin-refresh">
              <RefreshCw size={14} /> Aggiorna
            </button>
            <button onClick={handleLogout} className="btn-ghost text-sm py-2 px-3" data-testid="admin-logout">
              <LogOut size={14} /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <h1 className="font-display text-3xl md:text-4xl text-white tracking-tighter">Pannello lead</h1>
        <p className="mt-2 text-slate-400">Richieste raccolte dal form contatti e dal quiz.</p>

        {/* Staff state import */}
        <StaffStateImport />

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Lead totali" value={stats?.total ?? "—"} tid="stat-total" />
          <StatCard label="Contatti diretti" value={stats?.contacts ?? "—"} tid="stat-contacts" />
          <StatCard label="Quiz completati" value={stats?.quizzes ?? "—"} tid="stat-quizzes" />
          <StatCard label="Top pacchetto"
            value={stats?.by_tier ? (Object.entries(stats.by_tier).sort((a, b) => b[1] - a[1])[0]?.[0] || "—") : "—"}
            transform={(v) => TIER_LABEL[v] || v}
            tid="stat-top-tier"
          />
        </div>

        {/* Filters */}
        <div className="mt-10 flex flex-col md:flex-row md:items-center gap-4 justify-between">
          <div className="flex items-center gap-2">
            {[{ k: "all", l: "Tutti" }, { k: "contact", l: "Contatti" }, { k: "quiz", l: "Quiz" }].map((f) => (
              <button
                key={f.k}
                onClick={() => setFilter(f.k)}
                data-testid={`admin-filter-${f.k}`}
                className={`px-4 py-2 rounded-full text-xs font-medium border transition ${
                  filter === f.k
                    ? "bg-gradient-to-r from-purple-500 to-blue-500 border-transparent text-white shadow-[0_0_20px_rgba(147,51,234,0.35)]"
                    : "border-white/10 bg-white/[0.03] text-slate-300 hover:border-white/25"
                }`}
              >{f.l}</button>
            ))}
          </div>
          <div className="relative w-full md:w-72">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca per nome, email…"
              data-testid="admin-search"
              className="w-full bg-white/[0.03] border border-white/10 rounded-xl pl-9 pr-4 py-2.5 text-white placeholder:text-slate-500 focus:border-purple-400/50 outline-none transition text-sm"
            />
          </div>
        </div>

        {/* Table */}
        <div className="mt-6 glass gradient-border rounded-3xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center gap-3 text-slate-400 py-16" data-testid="admin-loading">
              <Loader2 className="animate-spin" size={16} /> Caricamento…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-slate-400" data-testid="admin-empty">
              Nessun lead da mostrare.
            </div>
          ) : (
            <div className="divide-y divide-white/5" data-testid="admin-leads-list">
              {filtered.map((l) => (
                <div key={l.id} className="p-5 md:p-6 hover:bg-white/[0.02] transition" data-testid={`lead-row-${l.id}`}>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4 min-w-0">
                      <div className={`h-10 w-10 shrink-0 rounded-xl flex items-center justify-center ${l.kind === "quiz" ? "bg-purple-500/20 text-purple-300" : "bg-blue-500/20 text-blue-300"}`}>
                        {l.kind === "quiz" ? <Sparkles size={16} /> : <MessageSquare size={16} />}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white font-medium truncate">{l.name}</span>
                          {l.kind === "quiz" && l.recommended_tier && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest bg-purple-500/20 text-purple-200 border border-purple-400/30">
                              {TIER_LABEL[l.recommended_tier] || l.recommended_tier}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-xs text-slate-400 flex items-center gap-3 flex-wrap">
                          <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 hover:text-white"><Mail size={11} />{l.email}</a>
                          {l.phone && <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1 hover:text-white"><Phone size={11} />{l.phone}</a>}
                          <span>{fmtDate(l.created_at)}</span>
                        </div>
                        {l.message && (
                          <p className="mt-2 text-sm text-slate-300 line-clamp-2 max-w-xl">{l.message}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => setSelected(l)}
                        data-testid={`lead-view-${l.id}`}
                        className="btn-ghost text-xs py-2 px-3"
                      >Dettagli</button>
                      <button
                        onClick={() => remove(l.id)}
                        data-testid={`lead-delete-${l.id}`}
                        aria-label="Elimina lead"
                        className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-white/5 transition"
                      ><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Detail modal */}
      {selected && (
        <div
          data-testid="lead-detail-modal"
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="w-full max-w-lg glass gradient-border rounded-3xl p-8 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-purple-300/80">{selected.kind === "quiz" ? "Lead Quiz" : "Contatto diretto"}</div>
                <h3 className="font-display text-2xl text-white mt-1">{selected.name}</h3>
              </div>
              <button onClick={() => setSelected(null)} data-testid="lead-detail-close" className="text-slate-400 hover:text-white p-2">×</button>
            </div>
            <dl className="space-y-3 text-sm">
              <Row label="Email"><a className="text-purple-300 hover:text-white" href={`mailto:${selected.email}`}>{selected.email}</a></Row>
              {selected.phone && <Row label="Telefono"><a className="text-purple-300 hover:text-white" href={`tel:${selected.phone}`}>{selected.phone}</a></Row>}
              <Row label="Ricevuto il">{fmtDate(selected.created_at)}</Row>
              {selected.recommended_tier && <Row label="Pacchetto consigliato">{TIER_LABEL[selected.recommended_tier] || selected.recommended_tier}</Row>}
              {selected.answers && (
                <>
                  <div className="pt-2 text-xs uppercase tracking-widest text-slate-400">Risposte quiz</div>
                  {Object.entries(selected.answers).map(([k, v]) => (
                    <Row key={k} label={k.replace("_", " ")}>{String(v)}</Row>
                  ))}
                </>
              )}
              {selected.message && (
                <>
                  <div className="pt-2 text-xs uppercase tracking-widest text-slate-400">Messaggio</div>
                  <p className="text-slate-200 whitespace-pre-wrap">{selected.message}</p>
                </>
              )}
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, transform, tid }) {
  const display = transform ? transform(value) : value;
  return (
    <div className="glass gradient-border rounded-2xl p-5" data-testid={tid}>
      <div className="text-xs uppercase tracking-widest text-slate-400">{label}</div>
      <div className="mt-2 font-display text-3xl text-white capitalize">{display}</div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
      <dt className="text-xs uppercase tracking-widest text-slate-400 sm:w-40 shrink-0 capitalize">{label}</dt>
      <dd className="text-slate-100 break-words">{children}</dd>
    </div>
  );
}

function StaffStateImport() {
  const { authAxios } = useAuth();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async () => {
    setError(""); setResult(null);
    if (!url.trim()) { setError("Inserisci l'URL del file state.json"); return; }
    setLoading(true);
    try {
      const r = await authAxios.post("/admin/staff-state/import", { url: url.trim() });
      setResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Importazione fallita");
    } finally { setLoading(false); }
  };

  return (
    <div className="mt-6 glass gradient-border rounded-2xl p-5 md:p-6" data-testid="staff-state-import">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-purple-300/80">Gestionale staff</div>
          <div className="font-display text-lg text-white">Importa workspace da URL</div>
          <div className="text-xs text-slate-500 mt-1">Carica progetti, task, calendario e settings nel database di produzione.</div>
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-2">
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://.../state.json"
          data-testid="staff-import-url"
          className="flex-1 bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-purple-400"
        />
        <button
          type="button"
          onClick={submit}
          disabled={loading}
          data-testid="staff-import-submit"
          className="btn-primary text-sm flex items-center justify-center gap-2 min-w-[140px]"
        >
          {loading ? <Loader2 className="animate-spin" size={14} /> : null}
          Importa dati
        </button>
      </div>
      {error && <div data-testid="staff-import-error" className="text-xs text-rose-300 mt-3">{error}</div>}
      {result && (
        <div data-testid="staff-import-result" className="text-xs text-emerald-300 mt-3">
          ✓ Importato — Social Whale: {result.counts.socialwhale_projects} progetti · La Palma: {result.counts.lapalma_projects} progetti, {result.counts.lapalma_tasks} task
        </div>
      )}
    </div>
  );
}
